import React, { useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  showDownloadNotification,
  showLoadingNotification,
} from 'services/api/notifications';
import { showProgressAlert } from 'services/alert/redux/actions';
import {
  startBackgroundExport,
  startBackgroundImport,
} from 'services/backgroundTasks';
import {
  BackgroundType,
  BackgroundAction,
} from 'services/backgroundTasks/types';
import { getErrorMessage } from 'helpers/common';
import { logErrorCtx } from 'app/logging';

interface UseSalesOrderFileOperationsParams {
  onExportStarted?: () => void;
  onImportInitiated?: () => void;
}

export function useSalesOrderFileOperations(
  params?: UseSalesOrderFileOperationsParams
) {
  const fileImportInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  const onTriggerImport = useCallback(() => {
    fileImportInputRef.current?.click();
  }, []);

  const onFileSelectedForImport = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files?.[0]) {
        return;
      }
      const uploadedFile = event.target.files[0];
      // Call directly, do not dispatch
      showLoadingNotification('Your Sales Order import has been initiated.');
      params?.onImportInitiated?.();
      event.target.value = ''; // Reset file input

      try {
        await dispatch(
          startBackgroundImport(BackgroundType.SalesOrder, uploadedFile) as any
        );
        dispatch(
          showProgressAlert(
            'Your Import to Sales Order has been initiated. This may take a few minutes to complete.',
            BackgroundType.SalesOrder,
            BackgroundAction.Import
          )
        );
      } catch (e) {
        const message = getErrorMessage(e);
        logErrorCtx('Error in startBackgroundImport for SalesOrder', {
          error: e instanceof Error ? e : undefined,
          description: message,
          component: 'useSalesOrderFileOperations',
        });
      }
    },
    [dispatch, params]
  );

  const onExport = useCallback(async () => {
    // Call directly, do not dispatch
    showDownloadNotification();
    try {
      await dispatch(startBackgroundExport(BackgroundType.SalesOrder) as any);
      params?.onExportStarted?.();
    } catch (e) {
      const message = getErrorMessage(e);
      logErrorCtx('Error in startBackgroundExport for SalesOrder', {
        error: e instanceof Error ? e : undefined,
        component: 'useSalesOrderFileOperations',
        description: message,
      });
    }
  }, [dispatch, params]);

  return {
    fileImportInputRef,
    onTriggerImport,
    onFileSelectedForImport,
    onExport,
  };
}
